/**
 * One transition on a state transition diagram, drawn border to border.
 *
 * The path comes from `edgeGeometry.ts`, fed the two nodes' ABSOLUTE boxes -
 * the only coordinates in which a sub-state and a state outside its machine can
 * be compared. The label is a DOM element rather than SVG text so it wraps, sits
 * above the line, and can be read by a screen reader as the transition it is.
 */

import { BaseEdge, EdgeLabelRenderer, useInternalNode } from '@xyflow/react'
import type { EdgeProps, InternalNode } from '@xyflow/react'
import Box from '@mui/material/Box'
import { selfLoopShape, transitionShape } from './edgeGeometry'
import type { TransitionEdge } from './stateDiagram'
import { LEAF_SIZE } from './stateDiagram'

function boxOf(node: InternalNode) {
  return {
    x: node.internals.positionAbsolute.x,
    y: node.internals.positionAbsolute.y,
    width: node.measured?.width ?? node.width ?? LEAF_SIZE.width,
    height: node.measured?.height ?? node.height ?? LEAF_SIZE.height,
  }
}

export default function TransitionEdgeView({
  id, source, target, label, data, selected, markerEnd, style,
}: EdgeProps<TransitionEdge>) {
  const from = useInternalNode(source)
  const to = useInternalNode(target)
  if (!from || !to) return null

  const shape = source === target
    ? selfLoopShape(boxOf(from))
    : transitionShape(boxOf(from), boxOf(to), data?.offset ?? 0)
  const text = String(label ?? '')
  const name = data?.transition.name

  return (
    <>
      <BaseEdge
        id={id}
        path={shape.path}
        markerEnd={markerEnd}
        interactionWidth={16}
        style={{ ...style, strokeWidth: selected ? 2.5 : 1.5 }}
      />
      {(text || name) && (
        <EdgeLabelRenderer>
          <Box
            className="nodrag nopan"
            aria-label={`Transition ${name ? `${name} ` : ''}${text}`.trim()}
            sx={{
              position: 'absolute',
              // Above the state boxes, like the edge itself: a label under a
              // box is a transition nobody can read.
              zIndex: 1001,
              transform: `translate(-50%, -50%) translate(${shape.label.x}px, ${shape.label.y}px)`,
              px: 0.5,
              borderRadius: 0.5,
              fontSize: 11,
              lineHeight: 1.3,
              bgcolor: 'background.paper',
              color: selected ? 'primary.main' : 'text.secondary',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {text || name}
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  )
}
